import type { Unit } from '@yawi/types';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { safeStorage } from '@/shared/utils/safeStorage';
import { useAuth } from './AuthContext';

const STORAGE_KEY_PREFIX = 'hommi_selected_unit:';

export interface SelectedUnitContextType {
  /** La unidad que el residente eligió "estar administrando". Null si no tiene ninguna. */
  selectedUnit: Unit | null;
  selectedUnitId: string | null;
  setSelectedUnitId: (unitId: string) => void;
  /** true solo cuando el residente tiene más de una unidad — controla si mostrar el selector. */
  hasMultipleUnits: boolean;
}

const SelectedUnitContext = createContext<SelectedUnitContextType | null>(null);

export const SelectedUnitProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const units = currentUser?.units ?? [];
  const storageKey = currentUser ? `${STORAGE_KEY_PREFIX}${currentUser.id}` : null;

  const [selectedUnitId, setSelectedUnitIdState] = useState<string | null>(null);

  // Al cambiar de usuario (o cargar sus unidades), resolver cuál queda seleccionada:
  // la guardada en storage si sigue siendo válida, si no la primaria, si no la primera.
  useEffect(() => {
    if (units.length === 0) {
      setSelectedUnitIdState(null);
      return;
    }

    const stored = storageKey ? safeStorage.getItem(storageKey) : null;
    const storedIsValid = stored && units.some((assignment) => assignment.unitId === stored);
    if (storedIsValid) {
      setSelectedUnitIdState(stored);
      return;
    }

    const primary = units.find((assignment) => assignment.isPrimary) ?? units[0];
    setSelectedUnitIdState(primary.unitId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, units.map((assignment) => assignment.unitId).join(',')]);

  const setSelectedUnitId = (unitId: string) => {
    setSelectedUnitIdState(unitId);
    if (storageKey) {
      safeStorage.setItem(storageKey, unitId);
    }
  };

  const selectedUnit = useMemo<Unit | null>(() => {
    const assignment = units.find((item) => item.unitId === selectedUnitId);
    return assignment?.unit ?? null;
  }, [units, selectedUnitId]);

  const value: SelectedUnitContextType = {
    selectedUnit,
    selectedUnitId,
    setSelectedUnitId,
    hasMultipleUnits: units.length > 1,
  };

  return (
    <SelectedUnitContext.Provider value={value}>
      {children}
    </SelectedUnitContext.Provider>
  );
};

export const useSelectedUnit = (): SelectedUnitContextType => {
  const ctx = useContext(SelectedUnitContext);
  if (!ctx) throw new Error('useSelectedUnit must be used within a SelectedUnitProvider');
  return ctx;
};
